import { DatePipe, NgFor, NgIf } from '@angular/common';
import { MetaDefinition, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { WordpressService } from 'rigshare-common';
import { SafeHtmlPipe } from 'rigshare-common';
import { LoadScriptsPipe } from 'rigshare-common';
import { CommentsComponent as CommentsComponent_1 } from 'rigshare-common';
import { CommentFormComponent } from 'rigshare-common';
import { ProductDetailCardComponent } from 'rigshare-common';
import { LoaderDirective } from 'rigshare-common';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
  MetaService,
  ProductDetailListGQL,
  ProductModel,
} from "rigshare-common";
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import {
  CommentsComponent,
  PostGQL,
  PostModel,
  WordPressPostModel,
} from "rigshare-common";

@Component({
  selector: "app-single",
  templateUrl: "./single.component.html",
  styleUrls: ["./single.component.css"],
  host: {
    class: "wp-embed-responsive",
  },
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    RouterLink,
    IonicModule,
    NgIf,
    LoaderDirective,
    NgFor,
    ProductDetailCardComponent,
    CommentFormComponent,
    CommentsComponent_1,
    DatePipe,
    LoadScriptsPipe,
    SafeHtmlPipe,
  ],
})
export class SingleComponent implements OnInit, OnDestroy {
  /**
   * The comments of the component.
   */
  @ViewChild("comments", { static: false }) comments?: CommentsComponent;

  /**
   * The loading state of the component.
   */
  loading: boolean = false;

  /**
   * The meta tags of the page.
   */
  metaTags: MetaDefinition[] = [];

  /**
   * The posts of the page.
   */
  post?: WordPressPostModel | null;

  /**
   * The related post of the blog post.
   */
  relatedPost?: PostModel | null;

  /**
   * The related products of the blog post.
   */
  relatedProducts?: ProductModel[] | null;

  /**
   * The similar posts of the component.
   */
  similarPosts?: WordPressPostModel[] | null;

  /**
   * The subscriptions of the page.
   */
  subs: Subscription = new Subscription();

  /**
   * Create a new instance of the component.
   */
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private meta: MetaService,
    private postGQL: PostGQL,
    private productDetailListGQL: ProductDetailListGQL,
    private title: Title,
    private wordpress: WordpressService
  ) {}

  /**
   * On component init.
   */
  ngOnInit(): void {
    const paramsSub = this.route?.params.subscribe((params) => {
      this.getPost(params["slug"]);
    });

    this.subs.add(paramsSub);
  }

  /**
   * On component destroy.
   */
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * Get post from the api.
   */
  async getPost(slug: string, params: any = {}): Promise<any> {
    this.post = null;
    this.loading = true;

    const id = slug?.split("-").pop() || "";

    this.wordpress
      .post(id, params)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (data) => {
          if (data?.status === 404) {
            this.router.navigate(["/not-found"], {
              skipLocationChange: true,
            });
          } else {
            this.post = new WordPressPostModel(data?.post);
            this.setMeta();
            this.getRelatedPost();
            this.getRelatedProducts();
            this.getSimilarPosts();
          }
        },
        error: (error) => {
          console.error(error);
          this.router.navigate(["/not-found"], {
            skipLocationChange: true,
          });
        },
      });
  }

  /**
   * Retrieve the related post of the blog post.
   */
  getRelatedPost() {
    this.relatedPost = null;

    if (!this.post?.custom_fields?.related_post) {
      return;
    }

    this.postGQL
      .fetch({
        id: this.post?.custom_fields?.related_post,
      })
      .subscribe({
        next: ({ data: { post } }) => {
          this.relatedPost = new PostModel(post);
        },
      });
  }

  /**
   * Retrieve the related post of the blog post.
   */
  getRelatedProducts() {
    this.relatedPost = null;

    if (!this.post?.custom_fields?.related_products?.length) {
      return;
    }

    const ids = this.post?.custom_fields?.related_products?.map(
      (p: { product_id: number }) => p.product_id
    );

    this.productDetailListGQL
      .fetch({
        in: ids,
      })
      .subscribe({
        next: ({ data: { products } }) => {
          this.relatedProducts = products?.data?.map(
            (p) => new ProductModel(p)
          );
        },
      });
  }

  /**
   * Get similar posts from the api.
   */
  async getSimilarPosts() {
    this.similarPosts = null;

    this.wordpress
      .posts({
        categories: this.post?.categories?.map((c) => c.id).join(",") || "",
        _embed: "true",
        exclude: `${this.post?.id}` || "",
        per_page: 3,
      })
      .subscribe({
        next: (data) => {
          if (!data?.posts) {
            return;
          }

          this.similarPosts = data?.posts?.map(
            (p: any) => new WordPressPostModel(p)
          );
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  /**
   * On comment added event handler.
   */
  onCommentAdded(comment: any): void {
    this.relatedPost?.addComment(comment);
    this.comments?.cd.detectChanges();
  }

  /**
   * Setup the meta data for the page.
   */
  setMeta(): void {
    this.metaTags = [];

    const description = this.post?.custom_fields?.description_tag || "";

    this.metaTags.push({
      name: "description",
      content: description,
    });

    this.metaTags.push({
      property: "og:description",
      content: description,
    });

    this.metaTags.push({
      name: "twitter:description",
      content: description,
    });

    let title;

    if (this.post?.custom_fields?.title_tag) {
      title = this.post.custom_fields.title_tag + " | RigShare";
    } else {
      title = this.post?.title?.rendered.replace(
        /(&#(\d+);)/g,
        (match, capture, charCode) => String.fromCharCode(charCode)
      );
      title = title + " | RigShare";
    }

    this.title.setTitle(title);
    this.metaTags.push({
      property: "og:title",
      content: title,
    });

    this.metaTags.push({
      name: "twitter:title",
      content: title,
    });

    if (this.post?.featured_image) {
      this.metaTags.push({
        property: "og:image",
        content: this.post?.featured_image?.media_details?.sizes
          ? this.post?.featured_image?.media_details?.sizes?.large?.source_url
          : this.post?.featured_image?.source_url,
      });

      this.metaTags.push(
        {
          name: "twitter:image",
          content: this.post?.featured_image?.media_details?.sizes
            ? this.post?.featured_image?.media_details?.sizes?.large?.source_url
            : this.post?.featured_image?.source_url,
        },
        {
          name: "twitter:card",
          content: "summary_large_image",
        }
      );
    }

    this.meta.create(this.metaTags);
  }
}
